import { Channel } from '../Channel';
import { FieldType } from '../Field';
import { FFuncType } from '../Formula/FormulaFunc';
import { DimensionNode, LayerNode, LayerRootNode, MeasureNode, NamesNode, } from './PivotNode';
export class PivotCollect {
    constructor(query, result) {
        this._query = query;
        this._result = result;
        this.buildSchema();
        this._collect = this.buildCollect();
    }
    get query() {
        return this._query;
    }
    get result() {
        return this._result;
    }
    get collect() {
        return this._collect;
    }
    get rootNode() {
        return this.result.root;
    }
    get rowNames() {
        return this.query.rows.fields.filter((f) => f.isNames);
    }
    get columnNames() {
        return this.query.columns.fields.filter((f) => f.isNames);
    }
    get rowDimensions() {
        return this.query.rows.fields.filter((f) => f.isDimension && !f.isNames);
    }
    get columnDimensions() {
        return this.query.columns.fields.filter((f) => f.isDimension && !f.isNames);
    }
    buildCollect() {
        let fieldValue = null;
        let currNode = null;
        let childNode = null;
        const currScope = {};
        let currLayer = null;
        const collectors = new Array();
        for (const [channel, axes] of Array.from(this.query.layers)) {
            for (const axis of axes) {
                collectors.push(new LayerCollector(axis, this.result));
            }
        }
        const rootNode = this.rootNode;
        const rowNames = this.rowNames;
        const columnNames = this.columnNames;
        const rowDimensions = this.rowDimensions;
        const columnDimensions = this.columnDimensions;
        return (row, index) => {
            fieldValue = null;
            childNode = null;
            currLayer = null;
            currNode = rootNode;
            for (const field of rowNames) {
                if (field.type === FieldType.VIEWNAMES) {
                    fieldValue = field.alias;
                    childNode = currNode.getChild(fieldValue);
                    if (childNode == null) {
                        childNode = new NamesNode(currNode, Channel.ROW, field);
                        currNode.setChild(fieldValue, childNode);
                    }
                    currNode = childNode;
                }
            }
            for (const field of columnNames) {
                if (field.type === FieldType.VIEWNAMES) {
                    fieldValue = field.alias;
                    childNode = currNode.getChild(fieldValue);
                    if (childNode == null) {
                        childNode = new NamesNode(currNode, Channel.COLUMN, field);
                        currNode.setChild(fieldValue, childNode);
                    }
                    currNode = childNode;
                }
            }
            for (const field of rowDimensions) {
                if (field.type === FieldType.VIEWCALC && field.formula.expr.type === FFuncType.SCALAR) {
                    currScope.row = row;
                    fieldValue = field.formula.expr.value(currScope);
                    childNode = currNode.getChild(fieldValue);
                    if (childNode == null) {
                        childNode = new DimensionNode(currNode, Channel.ROW, field, fieldValue);
                        currNode.setChild(fieldValue, childNode);
                    }
                    currNode = childNode;
                }
            }
            for (const field of columnDimensions) {
                if (field.type === FieldType.VIEWCALC && field.formula.expr.type === FFuncType.SCALAR) {
                    currScope.row = row;
                    fieldValue = field.formula.expr.value(currScope);
                    childNode = currNode.getChild(fieldValue);
                    if (childNode == null) {
                        childNode = new DimensionNode(currNode, Channel.COLUMN, field, fieldValue);
                        currNode.setChild(fieldValue, childNode);
                    }
                    currNode = childNode;
                }
            }
            fieldValue = '$LAYER$';
            childNode = currNode.getChild(fieldValue);
            if (childNode == null) {
                childNode = new LayerRootNode(currNode);
                currNode.setChild(fieldValue, childNode);
            }
            currNode = childNode;
            for (const collector of collectors) {
                fieldValue = collector.layer.name;
                childNode = currNode.getChild(fieldValue);
                if (childNode == null) {
                    childNode = new LayerNode(currNode, fieldValue);
                    currNode.setChild(fieldValue, childNode);
                }
                else {
                    childNode = childNode;
                }
                collector.collect(childNode, row, index);
            }
        };
    }
    buildSchema() {
        for (const field of this.rowNames) {
            this.result.getResultSet().addSchemaField(field);
        }
        for (const field of this.columnNames) {
            this.result.getResultSet().addSchemaField(field);
        }
        for (const field of this.rowDimensions) {
            this.result.getResultSet().addSchemaField(field);
        }
        for (const field of this.columnDimensions) {
            this.result.getResultSet().addSchemaField(field);
        }
    }
}
export class LayerCollector {
    constructor(axis, result) {
        this._layer = axis;
        this._result = result;
        this.buildSchema();
        this._collect = this.buildCollect();
    }
    get layer() {
        return this._layer;
    }
    get result() {
        return this._result;
    }
    get collect() {
        return this._collect;
    }
    get values() {
        return this.layer.query.values.fields;
    }
    get dimensions() {
        return this.layer.dimensions;
    }
    get measures() {
        return this.layer.measures;
    }
    buildCollect() {
        let fieldValue = null;
        const currScope = {};
        let currNode = null;
        let childNode = null;
        let currField = null;
        const values = this.values;
        const dimensions = this.dimensions;
        const measures = new Map(this.measures);
        switch (this.layer.channel) {
            case Channel.ROW:
                measures.set(Channel.COLUMN, new Array());
                for (const field of this.layer.query.columns.measures) {
                    measures.get(Channel.COLUMN).push(field);
                }
                break;
            case Channel.COLUMN:
                measures.set(Channel.ROW, new Array());
                for (const field of this.layer.query.rows.measures) {
                    measures.get(Channel.ROW).push(field);
                }
                break;
        }
        return (node, row, index) => {
            fieldValue = null;
            childNode = null;
            currNode = node;
            for (const [channel, fields] of Array.from(dimensions)) {
                for (let i = 0, len = fields.length; i < len; i++) {
                    currField = fields[i];
                    switch (currField.type) {
                        case FieldType.VIEWCALC:
                            if (currField.formula.expr.type === FFuncType.SCALAR) {
                                currScope.row = row;
                                fieldValue = currField.formula.expr.value(currScope);
                                childNode = currNode.getChild(fieldValue);
                                if (childNode == null) {
                                    childNode = new DimensionNode(currNode, channel, currField, fieldValue);
                                    currNode.setChild(fieldValue, childNode);
                                    currNode = childNode;
                                }
                            }
                            break;
                        case FieldType.VIEWNAMES:
                            fieldValue = currField.alias;
                            childNode = currNode.getChild(fieldValue);
                            if (childNode == null) {
                                childNode = new NamesNode(currNode, channel, currField);
                                currNode.setChild(fieldValue, childNode);
                                currNode = childNode;
                            }
                            break;
                    }
                    currNode = childNode;
                }
            }
            fieldValue = '$MEASURES$';
            childNode = currNode.getChild(fieldValue);
            if (childNode == null) {
                childNode = new MeasureNode(currNode, this.layer.channel);
                currNode.setChild(fieldValue, childNode);
                currNode = childNode;
            }
            else {
                currNode = childNode;
            }
            for (const [channel, fields] of Array.from(measures)) {
                for (let i = 0, len = fields.length; i < len; i++) {
                    currField = fields[i];
                    currNode.addField(channel, currField);
                    if (currField.type === FieldType.VIEWVALUES) {
                        for (let v = 0, vlen = values.length; v < vlen; v++) {
                            currNode.addField(Channel.VALUE, values[v]);
                        }
                    }
                }
            }
            currNode.collect(row);
        };
    }
    buildSchema() {
        const values = this.values;
        const dimensions = this.dimensions;
        const measures = new Map(this.measures);
        switch (this.layer.channel) {
            case Channel.ROW:
                measures.set(Channel.COLUMN, new Array());
                for (const field of this.layer.query.columns.measures) {
                    measures.get(Channel.COLUMN).push(field);
                }
                break;
            case Channel.COLUMN:
                measures.set(Channel.ROW, new Array());
                for (const field of this.layer.query.rows.measures) {
                    measures.get(Channel.ROW).push(field);
                }
                break;
        }
        for (const [channel, fields] of Array.from(dimensions)) {
            for (const field of fields) {
                this.result.getResultSet(this.layer.name).addSchemaField(field);
            }
        }
        for (const [channel, fields] of Array.from(measures)) {
            for (const field of fields) {
                if (field.type === 'VIEWVALUES') {
                    for (let v = 0, vlen = values.length; v < vlen; v++) {
                        this.result.getResultSet(this.layer.name).addSchemaField(values[v]);
                    }
                }
                else {
                    this.result.getResultSet(this.layer.name).addSchemaField(field);
                }
            }
        }
    }
}
